import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from '@tanstack/react-router'

export const DEFAULT_LANGUAGE = 'en' as const
export const SUPPORTED_LANGUAGES = ['zh', 'en'] as const
export type Language = typeof SUPPORTED_LANGUAGES[number]

// Extract language from URL path
// No prefix = English (default), /zh = Chinese
export function getLanguageFromPath(pathname: string): Language {
  const segments = pathname.split('/').filter(Boolean)
  const firstSegment = segments[0]
  if (firstSegment === 'zh') {
    return 'zh'
  }
  return DEFAULT_LANGUAGE // No prefix means English
}

// Remove language prefix from path
export function removeLanguageFromPath(pathname: string): string {
  // Only zh has a prefix to remove
  if (pathname.startsWith('/zh')) {
    return pathname.replace(/^\/zh/, '') || '/'
  }
  return pathname
}

// Add language prefix to path
// English (default) = no prefix, Chinese = /zh prefix
export function addLanguageToPath(pathname: string, lang: Language): string {
  const cleanPath = removeLanguageFromPath(pathname)
  if (lang === DEFAULT_LANGUAGE) {
    // English uses no prefix
    return cleanPath === '' ? '/' : cleanPath
  }
  // Chinese uses /zh prefix
  if (cleanPath === '/' || cleanPath === '') {
    return `/${lang}`
  }
  return `/${lang}${cleanPath}`
}

interface LanguageWrapperProps {
  children: React.ReactNode
  defaultLanguage?: Language
}

export function LanguageWrapper({ children, defaultLanguage = 'en' }: LanguageWrapperProps) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    // Get language from URL
    const urlLang = getLanguageFromPath(location.pathname)
    
    // Sync i18n with URL language
    if (i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang)
    }
  }, [location.pathname, i18n])

  // Listen to i18n language changes and update URL
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const currentLang = getLanguageFromPath(location.pathname)
      if (currentLang !== lng && SUPPORTED_LANGUAGES.includes(lng as Language)) {
        const newPath = addLanguageToPath(location.pathname, lng as Language)
        navigate({ to: newPath + location.search + location.hash, replace: true })
      }
    }

    i18n.on('languageChanged', handleLanguageChange)
    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n, location, navigate])

  return <>{children}</>
}
